import React from "react";
import { Table, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FiArrowRight, FiInfo } from "react-icons/fi";
import { useTranslation } from "react-i18next"; 

const getInitials = (name) => {
    if (!name) return "EQ";
    return name.substring(0, 1).toUpperCase();
};

const calculateStats = (members, delegations = []) => {
    const total = members.length;
    if (total === 0) return { gender: [], workplaces: [] };
    
    const workplaceCounts = {};
    members.forEach(m => {
        const delegationId = m.user?.delegation_id;
        
        const delegationObj = delegations.find(d => d.id === delegationId);
        
        const workplaceName = delegationObj ? delegationObj.name : "Sem Delegação";
        
        workplaceCounts[workplaceName] = (workplaceCounts[workplaceName] || 0) + 1;
    });

    const pastelColors = [
        "#93c5fd", 
        "#a7f3d0", 
        "#fde047", 
        "#fbcfe8", 
        "#c4b5fd", 
        "#fed7aa"  
    ];

    const workplaces = Object.keys(workplaceCounts).map((name, index) => ({
        label: name,
        percent: (workplaceCounts[name] / total) * 100,
        color: pastelColors[index % pastelColors.length]
    }));

    return {
        workplaces: workplaces
    };
};

const TeamTable = ({ teams, delegations, onViewTeam }) => {
    const { t } = useTranslation();

    return (
        <Table hover responsive className="mb-0 align-middle bg-surface">
            <thead style={{ borderBottom: "var(--border-thin)" }}>
                <tr>
                    <th className="txt-main fw-semibold py-3 px-4 border-0" style={{ width: "25%" }}>
                        {t("components.TeamTable.teamName")} <span className="ms-1">&#9652;</span>
                    </th>
                    <th className="txt-main fw-semibold py-3 px-4 border-0" style={{ width: "10%" }}>
                        {t("components.TeamTable.members")}
                    </th>
                    <th className="txt-main fw-semibold py-3 px-4 border-0" style={{ width: "20%" }}>
                        {t("components.TeamTable.averages")}
                    </th>
                    <th className="txt-main fw-semibold py-3 px-4 border-0" style={{ width: "20%" }}>
                        <FiInfo className="me-1 txt-subtle" /> {t("components.TeamTable.workplace")}
                    </th>
                    <th className="border-0" style={{ width: "5%" }}></th>
                </tr>
            </thead>
           <tbody>
                {teams && teams.length > 0 ? (
                    teams.map((team, index) => {
                        const avatarColors = ["#ffe4e6", "#4c1d95", "#e0e7ff", "#fef3c7"];
                        const textColors = ["#e11d48", "#ffffff", "#3730a3", "#d97706"];
                        const colorIndex = index % avatarColors.length;

                        const allMembers = team.memberships || team.members || [];
                        const members = allMembers.filter(m => !m.left_at); 
                        const memberCount = members.length;
                        
                        const stats = calculateStats(members, delegations);

                        return (
                            <tr key={team.id} style={{ borderBottom: "var(--border-thin)", cursor: "pointer" }} onClick={() => onViewTeam(team)}>
                                
                                <td className="py-3 px-4 border-0">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="d-flex align-items-center justify-content-center rounded-circle fw-bold"
                                            style={{ width: "35px", height: "35px", backgroundColor: avatarColors[colorIndex], color: textColors[colorIndex], fontSize: "14px" }}>
                                            {getInitials(team.name)}
                                        </div>
                                        <span className="fw-semibold txt-main">{team.name}</span>
                                    </div>
                                </td>
                                
                                <td className="py-3 px-4 border-0 txt-main fw-semibold">{memberCount}</td>

                                <td className="py-3 px-4 border-0">
                                    <div className="txt-subtle text-small">
                                        {memberCount > 5 ? t("components.TeamTable.age") : t("components.TeamTable.na")}
                                    </div>
                                    <div className="txt-subtle text-small">
                                        {t("components.TeamTable.seniority")}: {memberCount > 5 ? `3,3 ${t("components.TeamTable.years")}` : `2,4 ${t("components.TeamTable.years")}`}
                                    </div>
                                </td>

                                <td className="py-3 px-4 border-0">
                                    <div className="d-flex gap-1 w-100 rounded overflow-hidden" style={{ height: "6px" }}>
                                        {stats.workplaces.length > 0 ? (
                                            stats.workplaces.map((wp, i) => (
                                                <OverlayTrigger key={i} placement="top" overlay={<Tooltip>{wp.label}: {wp.percent.toFixed(0)}%</Tooltip>}>
                                                    <div style={{ width: `${wp.percent}%`, backgroundColor: wp.color }}></div>
                                                </OverlayTrigger>
                                            ))
                                        ) : (
                                            <div style={{ width: "100%", backgroundColor: "#e5e7eb" }}></div>
                                        )}
                                    </div>
                                </td>

                                <td className="py-3 px-4 border-0 text-end">
                                    <Button variant="light" className="rounded-circle d-flex align-items-center justify-content-center p-0 shadow-sm" style={{ width: "32px", height: "32px", border: "var(--border-thin)" }}>
                                        <FiArrowRight className="txt-subtle" />
                                    </Button>
                                </td>
                            </tr>
                        );
                    })
                ) : (
                    <tr>
                        <td colSpan="6" className="text-center py-5 txt-subtle bg-surface">
                            {t("components.TeamTable.noTeamsFound")}
                        </td>
                    </tr>
                )}
            </tbody>
        </Table>
    );
};

export default TeamTable;